import TileLayer from "ol/layer/Tile";
import VectorTileLayer from "ol/layer/VectorTile";
import TileWMS from "ol/source/TileWMS";
import VectorTileSource from "ol/source/VectorTile";
import MVT from "ol/format/MVT";
import { Fill, Style } from "ol/style";
import type { StyleLike } from "ol/style/Style";
import type { FeatureLike } from "ol/Feature";
import Feature from "ol/Feature";
import RenderFeature from "ol/render/Feature";
import type { LoadFunction } from "ol/Tile";
import { transformExtent } from "ol/proj";
import HttpClient from "../../auth/authApi";
import { scaleLevels } from "../../../constants/search";

const WGS84 = "EPSG:4326";
const WEB_MERCATOR = "EPSG:3857";
const BBOX_VIEWPARAM_DECIMALS = 8;

/** Même base URL que `OLMap` (proxy dev : `/geoserver` → API → GeoServer). */
export const GEOSERVER_WMS_URL = "geoserver/wms";

/** Opacité commune des couches de risque dans l’éditeur d’override (WMS preview + MVT). */
export const RISK_MAP_LAYER_OPACITY = 0.48;

const TRANSPARENT_PNG_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==";

/** Parse `VIEWPARAMS` GeoServer (`k:v;k2:v2`, échappements `\;` `\:`). */
export function parseGeoServerViewParams(viewparams: string | null | undefined): Record<string, string> {
  if (!viewparams) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const pair of viewparams.split(";")) {
    if (!pair) {
      continue;
    }
    const colon = pair.indexOf(":");
    if (colon < 0) {
      continue;
    }
    const key = pair.slice(0, colon).trim();
    const raw = pair.slice(colon + 1);
    out[key] = raw.replace(/\\;/g, ";").replace(/\\:/g, ":");
  }
  return out;
}

function parseWmsBboxParam(src: string): [number, number, number, number] | null {
  try {
    const url = src.startsWith("http") ? new URL(src) : new URL(src, window.location.origin);
    const bboxRaw = url.searchParams.get("BBOX") ?? url.searchParams.get("bbox");
    if (!bboxRaw) {
      return null;
    }
    const parts = bboxRaw.split(",").map((s) => Number.parseFloat(s.trim()));
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) {
      return null;
    }
    return [parts[0]!, parts[1]!, parts[2]!, parts[3]!];
  } catch {
    return null;
  }
}

/** BBOX tuile WMS (EPSG:3857) → coins WGS84 pour VIEWPARAMS GeoServer (fn_*_final). */
export function wmsTileBboxToWgs84ViewParams(
  bbox3857: [number, number, number, number],
): { minx: string; miny: string; maxx: string; maxy: string } {
  const extent4326 = transformExtent(bbox3857, WEB_MERCATOR, WGS84);
  const fmt = (n: number) => n.toFixed(BBOX_VIEWPARAM_DECIMALS);
  return {
    minx: fmt(extent4326[0]),
    miny: fmt(extent4326[1]),
    maxx: fmt(extent4326[2]),
    maxy: fmt(extent4326[3]),
  };
}

/**
 * Fusionne VIEWPARAMS existants + statiques + bbox tuile (minx..maxy WGS84).
 * Utilisé par les couches finales (fn_*_final) pour limiter le calcul PostGIS à la tuile.
 */
export function injectTileBboxIntoWmsUrl(
  src: string,
  staticViewParams?: Record<string, string>,
): string {
  const url = src.startsWith("http") ? new URL(src) : new URL(src, window.location.origin);
  const merged: Record<string, string> = {
    ...parseGeoServerViewParams(url.searchParams.get("VIEWPARAMS")),
    ...staticViewParams,
  };
  const bbox = parseWmsBboxParam(src);
  if (bbox) {
    Object.assign(merged, wmsTileBboxToWgs84ViewParams(bbox));
  }
  const viewparams = formatGeoServerViewParams(merged);
  if (viewparams) {
    url.searchParams.set("VIEWPARAMS", viewparams);
  } else {
    url.searchParams.delete("VIEWPARAMS");
  }
  const pathAndQuery = `${url.pathname}${url.search}`;
  return src.startsWith("http") ? url.toString() : pathAndQuery;
}

export type AuthenticatedGeoServerTileLoaderOptions = {
  /** VIEWPARAMS fixes (ex. tenant_id) fusionnés à chaque tuile. */
  staticViewParams?: Record<string, string>;
  /**
   * Lit BBOX (EPSG:3857) de chaque GetMap et ajoute minx/miny/maxx/maxy (EPSG:4326)
   * pour les vues SQL fn_*_final.
   */
  injectTileBboxViewParams?: boolean;
};

/**
 * Charge les tuiles WMS avec le client axios authentifié (Bearer),
 * comme dans `OLMap`, pour éviter les 401 sur GeoServer derrière la même session.
 */
export function createAuthenticatedGeoServerTileLoader(
  options?: AuthenticatedGeoServerTileLoaderOptions,
): LoadFunction {
  const { staticViewParams, injectTileBboxViewParams = false } = options ?? {};
  return async function tileLoader(tile: any, src: string) {
    const requestUrl = injectTileBboxViewParams
      ? injectTileBboxIntoWmsUrl(src, staticViewParams)
      : src;
    try {
      const response = await HttpClient.get(requestUrl, { responseType: "arraybuffer" });
      const arrayBufferView = new Uint8Array(response.data);
      const blob = new Blob([arrayBufferView], { type: "image/png" });
      const urlCreator = window.URL || (window as unknown as { webkitURL?: typeof URL }).webkitURL;
      const imageUrl = urlCreator.createObjectURL(blob);
      tile.getImage().src = imageUrl;
    } catch (err) {
      console.warn("GeoServer tile load failed (tile not displayed):", requestUrl, err);
      tile.getImage().src = TRANSPARENT_PNG_DATA_URL;
    }
  };
}

/** Encode les viewparams GeoServer (`key:val;key2:val2`). Omet les valeurs vides (ex. tenant_id global). */
export function formatGeoServerViewParams(
  params: Record<string, string>
): string {
  return Object.entries(params)
    .filter(([, v]) => String(v).trim() !== "")
    .map(([k, v]) => `${k}:${String(v).replace(/;/g, "\\;").replace(/:/g, "\\:")}`)
    .join(";");
}

export type GeoServerRiskTileLayerOptions = {
  /** Valeur complète du paramètre WMS `LAYERS` */
  layers: string;
  /** Préfixe optionnel pour `STYLES` (ex. `data4home:multi_rcp_selection_26`) */
  styles?: string;
  /** Paramètres SQL view GeoServer, convertis en `VIEWPARAMS` */
  viewParams?: Record<string, string>;
  opacity?: number;
  zIndex?: number;
  className?: string;
  /** Par défaut : chargeur avec HttpClient + image transparente en erreur */
  tileLoadFunction?: LoadFunction;
  /** Si false, pas de tileLoadFunction custom (tuiles via `<img>` standard) */
  useAuthenticatedTileLoader?: boolean;
  /** Zoom minimal / maximal View OL (visibilité de la couche). */
  minZoom?: number;
  maxZoom?: number;
  /** Anti-cache WMS (`_v`), comme pour les tuiles MVT. */
  cacheBustId?: number;
  /**
   * Couches fn_*_final : injecte minx..maxy (WGS84) par tuile dans VIEWPARAMS
   * (solution A — bbox dérivée du paramètre WMS BBOX).
   */
  injectTileBboxViewParams?: boolean;
};

/**
 * Couche tuilée WMS GeoServer pour les cartes de risque (même pattern que `OLMap`).
 */
export function createGeoServerRiskTileLayer(
  options: GeoServerRiskTileLayerOptions
): TileLayer<TileWMS> {
  const {
    layers,
    styles = "",
    viewParams,
    opacity = 0.6,
    zIndex = 9,
    className = "data-layer",
    tileLoadFunction,
    useAuthenticatedTileLoader = true,
    minZoom,
    maxZoom,
    cacheBustId,
    injectTileBboxViewParams = false,
  } = options;

  const params: Record<string, string | boolean> = {
    LAYERS: layers,
    TILED: true,
    // STYLES: styles,
    // TRANSPARENT: true,
    FORMAT: "image/png",
    _v: String(cacheBustId ?? Date.now()),
  };

  if (viewParams) {
    const viewparams = formatGeoServerViewParams(viewParams);
    if (viewparams) {
      params.VIEWPARAMS = viewparams;
    }
  }

  const resolvedTileLoader =
    tileLoadFunction ??
    (useAuthenticatedTileLoader
      ? createAuthenticatedGeoServerTileLoader({
          staticViewParams: viewParams,
          injectTileBboxViewParams,
        })
      : undefined);

  return new TileLayer({
    source: new TileWMS({
      url: GEOSERVER_WMS_URL,
      params,
      serverType: "geoserver",
      transition: 0,
      ...(resolvedTileLoader ? { tileLoadFunction: resolvedTileLoader } : {}),
      projection: "EPSG:3857",
    }),
    className,
    zIndex,
    opacity,
    ...(minZoom != null ? { minZoom } : {}),
    ...(maxZoom != null ? { maxZoom } : {}),
  });
}

/** Style par défaut pour les polygones MVT (couche publiée, hors éditeur d’override). */
const MVT_PUBLISHED_STYLE = new Style({
  fill: new Fill({ color: "rgba(33, 150, 243, 0.22)" }),
});

/** Aligné sur `risk.flood_risk_level_from_scenario` (PostGIS). */
export function floodRiskLevelFromScenario(scenario: string | null | undefined): number {
  if (scenario == null || scenario === "") {
    return 0;
  }
  const map: Record<string, number> = {
    "01For": 4,
    "01Forcc_ct": 4,
    "02Moy": 3,
    "03Mcc": 2,
    "03Mcc_ct": 2,
    "04Fai": 1,
    "04Faicc_ct": 1,
  };
  return map[String(scenario).trim()] ?? 0;
}

/** Aligné sur `risk.submersion_risk_level_from_niveau_eau` (PostGIS). */
export function submersionRiskLevelFromNiveauEau(
  niveauEau: number | null | undefined,
): number {
  // Ancien 0 → 3, ancien 1+ → 4 (voir submersion.postgres.ts pour le détail).
  // if (niveauEau == null || Number.isNaN(niveauEau) || niveauEau <= 0) {
  //   return 3;
  // }
  // return 4;

  if (niveauEau == null || Number.isNaN(niveauEau) || niveauEau <= 0) return 0;
  if (niveauEau <= 1.5) return 1;
  if (niveauEau <= 2.5) return 2;
  if (niveauEau <= 4.5) return 3;
  return 4;
}

/** Aligné sur `risk.coastal_erosion_risk_level_from_taux` (PostGIS) pour le référentiel côtier. */
export function coastalErosionRiskLevelFromTaux(taux: number): number {
  if (taux == null || Number.isNaN(taux) || taux === -9999) {
    return 0;
  }
  const a = Math.abs(taux);
  if (a > 3) {
    return 4;
  }
  if (a > 1.5) {
    return 3;
  }
  if (a > 0.5) {
    return 2;
  }
  if (a > 0.2) {
    return 1;
  }
  return 0;
}

function mvtFeatureGet(feature: FeatureLike, key: string): unknown {
  if (feature instanceof Feature) {
    return feature.get(key);
  }
  if (feature instanceof RenderFeature) {
    return feature.get(key);
  }
  const anyF = feature as { get?: (k: string) => unknown };
  return typeof anyF.get === "function" ? anyF.get(key) : undefined;
}

/**
 * Niveau de risque 0–5 (ou -1 si inconnu) depuis les attributs MVT GeoServer.
 * Utilise `risk_level` / variantes ; sinon `taux` pour le référentiel coastal (même logique que PostGIS).
 */
export function readRiskLevelFromMvtFeature(feature: FeatureLike): number {
  const keys = ["risk_level", "Risk_level", "RISK_LEVEL", "riskLevel", "rl"];
  for (const k of keys) {
    const v = mvtFeatureGet(feature, k);
    if (v === undefined || v === null || v === "") {
      continue;
    }
    const n = typeof v === "number" ? v : Number(String(v).trim());
    if (Number.isFinite(n) && n >= -1 && n <= 5) {
      return Math.trunc(n);
    }
  }
  const tauxRaw =
    mvtFeatureGet(feature, "taux") ??
    mvtFeatureGet(feature, "Taux") ??
    mvtFeatureGet(feature, "TAUX");
  if (tauxRaw !== undefined && tauxRaw !== null && tauxRaw !== "") {
    const taux = typeof tauxRaw === "number" ? tauxRaw : Number(String(tauxRaw).trim());
    if (Number.isFinite(taux)) {
      return coastalErosionRiskLevelFromTaux(taux);
    }
  }
  const niveauEauRaw =
    mvtFeatureGet(feature, "niveau_eau") ??
    mvtFeatureGet(feature, "niveauEau") ??
    mvtFeatureGet(feature, "NIVEAU_EAU");
  if (niveauEauRaw !== undefined && niveauEauRaw !== null && niveauEauRaw !== "") {
    const niveauEau =
      typeof niveauEauRaw === "number"
        ? niveauEauRaw
        : Number(String(niveauEauRaw).trim());
    if (Number.isFinite(niveauEau)) {
      return submersionRiskLevelFromNiveauEau(niveauEau);
    }
  }
  const scenarioRaw =
    mvtFeatureGet(feature, "scenario") ?? mvtFeatureGet(feature, "Scenario");
  if (scenarioRaw !== undefined && scenarioRaw !== null && scenarioRaw !== "") {
    return floodRiskLevelFromScenario(String(scenarioRaw));
  }
  return -1;
}

/**
 * Ordre de rendu MVT : risque faible d’abord, risque élevé au-dessus (évite le z-fighting
 * entre polygones opaques superposés quand l’opacité est portée par la couche).
 */
export function mvtRiskLevelRenderOrder(a: FeatureLike, b: FeatureLike): number {
  return readRiskLevelFromMvtFeature(a) - readRiskLevelFromMvtFeature(b);
}

/**
 * Style MVT par polygone : couleurs `scaleLevels` (search), remplissage opaque (comme SLD GeoServer).
 * L’opacité visuelle est portée par `layer.setOpacity`, pas par le canal alpha du fill.
 */
export function createMvtRiskLevelStyleFromScaleLevels(): (feature: FeatureLike) => Style {
  return (feature: FeatureLike) => {
    const level = readRiskLevelFromMvtFeature(feature);
    const hex = scaleLevels[level]?.color ?? scaleLevels[-1]?.color ?? "#888888";
    return new Style({
      fill: new Fill({ color: hex }),
    });
  };
}

export type GeoServerMvtFromWmsResult = {
  layer: VectorTileLayer<VectorTileSource>;
  wmsSource: TileWMS;
  vectorSource: VectorTileSource;
  /** Met à jour le paramètre `_v` (anti-cache HTTP) et recharge les tuiles MVT. */
  refreshTiles: () => void;
};

/**
 * Tuiles vectorielles Mapbox (MVT) servies par GeoServer via **WMS** (FORMAT MVT),
 * comme dans le tutoriel GeoServer Vector Tiles.
 * Les URLs sont celles de `TileWMS` ; le chargement des tuiles utilise `HttpClient` (Bearer).
 */
export function createGeoServerMvtFromWmsLayer(options: {
  layers: string;
  styles?: string;
  viewParams?: Record<string, string>;
  /** Valeur initiale du paramètre WMS `_v` (évite le cache dès le premier chargement). */
  cacheBustId?: number;
  opacity?: number;
  zIndex?: number;
  className?: string;
  /** Style par entité (recommandé : `createMvtRiskLevelStyleFromScaleLevels()`). */
  style?: StyleLike;
  /** Zoom minimal View OL : couche masquée en dessous (pas de requêtes tuiles). */
  minZoom?: number;
  maxZoom?: number;
}): GeoServerMvtFromWmsResult {
  const {
    layers,
    styles = "",
    viewParams,
    cacheBustId,
    opacity = 0.55,
    zIndex = 9,
    className = "risk-override-published-mvt-layer",
    style = MVT_PUBLISHED_STYLE,
    minZoom,
    maxZoom,
  } = options;

  const mvtFormat = new MVT();

  const params: Record<string, string | boolean> = {
    LAYERS: layers,
    // STYLES: styles,
    TILED: false,
    FORMAT: "application/vnd.mapbox-vector-tile",
    // TRANSPARENT: true,
    _v: String(cacheBustId ?? Date.now()),
  };

  if (viewParams) {
    const viewparams = formatGeoServerViewParams(viewParams);
    if (viewparams) {
      params.VIEWPARAMS = viewparams;
    }
  }

  const wmsSource = new TileWMS({
    url: GEOSERVER_WMS_URL,
    params,
    serverType: "geoserver",
    transition: 0,
    projection: "EPSG:3857",
    hidpi: false,
  });

  const vectorSource = new VectorTileSource({
    format: mvtFormat,
    projection: "EPSG:3857",
    tileUrlFunction: (tileCoord, pixelRatio, projection) =>
      wmsSource.tileUrlFunction(tileCoord, pixelRatio, projection),
    tileLoadFunction: (tile: any, url: string) => {
      if (!url) {
        return;
      }
      tile.setLoader((extent: unknown, _resolution: number, proj: unknown) => {
          HttpClient.get(url, { responseType: "arraybuffer" })
            .then((response) => {
              const data = response.data as ArrayBuffer;
              const features = mvtFormat.readFeatures(data, {
                extent: extent as number[],
                featureProjection: proj as import("ol/proj/Projection").default,
              });
              tile.setFeatures(features);
            })
            .catch((err) => {
              console.warn("GeoServer MVT (WMS) tile load failed:", url, err);
              tile.setFeatures([]);
            });
      });
    },
    transition: 0,
    wrapX: true,
  });

  const layer = new VectorTileLayer({
    source: vectorSource,
    style,
    className,
    zIndex,
    opacity,
    renderMode: "hybrid",
    renderOrder: mvtRiskLevelRenderOrder,
    ...(minZoom != null ? { minZoom } : {}),
    ...(maxZoom != null ? { maxZoom } : {}),
  });

  const refreshTiles = () => {
    const stamp = String(Date.now());
    wmsSource.updateParams({ _v: stamp });
    /* OpenLayers garde des tuiles MVT par clé de source ; sans nouveau `setKey`,
     * les `VectorRenderTile` déjà en cache peuvent rester affichées malgré un `_v` WMS différent. */
    (vectorSource as unknown as { setKey: (key: string) => void }).setKey(stamp);
    vectorSource.refresh();
  };

  return { layer, wmsSource, vectorSource, refreshTiles };
}
